import React from "react";
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

const CVTemplateModern = ({ user, projects, language = 'id' }) => {
  const {
    email,
    photo,
    job,
    phone,
    location,
    summary,
    skills = [],
    experience = [],
    education = [],
  } = user || {};

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = education.length > 0 ? 
    categorizeEducation(education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break max-w-4xl mx-auto bg-white p-4 sm:p-6 md:p-8 rounded-lg print:p-10 print:rounded-none">
      {/* Header: Nama dan Foto */}
      <div className="cv-section flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4 sm:gap-6 mb-4 sm:mb-6 avoid-break">
        <div className="text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">{getFormattedUserName(user)}</h1>
          <p className="text-base sm:text-lg text-gray-600">{job}</p>
          <p className="text-xs sm:text-sm text-gray-500">{email}</p>
          <p className="text-xs sm:text-sm text-gray-500">{phone}</p>
          <p className="text-xs sm:text-sm text-gray-500">{location}</p>
        </div>
        <div className="flex-shrink-0">
          <img
            src={photo || "/assets/images/default-avatar.png"}
            alt="Avatar"
            className="cv-photo w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-lg object-cover border-2 border-gray-200 print:w-32 print:h-32 print:border-gray-400"
          />
        </div>
      </div>

      {/* Ringkasan */}
      {summary && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <hr className="my-2 sm:my-3 border-gray-300" />
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold mb-2 text-gray-800">Ringkasan</h2>
          <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-line text-justify">{summary}</p>
          <hr className="my-2 sm:my-3 border-gray-300" />
        </div>
      )}

      {/* Keahlian */}
      {skills?.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold mb-2 text-gray-800">{t('skills', language)}</h2>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, idx) => (
              <span
                key={idx}
                className="bg-gradient-to-r from-blue-100 to-blue-100 text-blue-800 px-3 py-1.5 text-xs sm:text-sm rounded-full font-medium print:bg-gray-200 print:text-gray-800"
              >
                {skill}
              </span>
            ))}
          </div>
          <hr className="my-3 border-gray-300" />
        </div>
      )}

      {/* Pengalaman */}
      {experience?.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold mb-3 text-gray-800 border-b-2 border-blue-500 pb-1">{t('experience', language)}</h2>
          {experience.map((exp, idx) => (
            <div key={idx} className="cv-experience-item mb-3 sm:mb-4 pl-0 sm:pl-3 border-l-0 sm:border-l-2 border-blue-200 avoid-break">
              <p className="font-bold text-sm sm:text-base text-gray-900">
                {exp.position}
              </p>
              <p className="font-semibold text-xs sm:text-sm text-blue-600">
                {exp.company}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 italic mb-1">{exp.period}</p>
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line text-justify">{exp.description}</p>
            </div>
          ))}
          <hr className="my-3 border-gray-300" />
        </div>
      )}

      {/* Pendidikan Tinggi */}
      {higherEducation.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold mb-3 text-gray-800 border-b-2 border-blue-500 pb-1">{t('higherEducation', language)}</h2>
          {higherEducation.map((edu, idx) => (
            <div key={idx} className="cv-education-item mb-3 avoid-break">
              <p className="font-bold text-sm sm:text-base text-gray-900">{edu.degree}</p>
              <p className="text-xs sm:text-sm text-blue-600 font-semibold">
                {edu.school || edu.institution}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 italic">{edu.year}</p>
            </div>
          ))}
          <hr className="my-3 border-gray-300" />
        </div>
      )}

      {/* Pendidikan Formal (K-12) */}
      {k12Education.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold mb-3 text-gray-800 border-b-2 border-blue-500 pb-1">{t('formalEducation', language)}</h2>
          {k12Education.map((edu, idx) => (
            <div key={idx} className="cv-education-item mb-3 avoid-break">
              <p className="font-bold text-sm sm:text-base text-gray-900">{edu.degree}</p>
              <p className="text-xs sm:text-sm text-blue-600 font-semibold">
                {edu.school || edu.institution}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 italic">{edu.year}</p>
            </div>
          ))}
          <hr className="my-3 border-gray-300" />
        </div>
      )}

      {/* Projek */}
      {projects?.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold mb-3 text-gray-800 border-b-2 border-green-500 pb-1">{t('projects', language)}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {projects.map((project, idx) => (
              <div
                key={idx}
                className="cv-project-item border-2 border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm bg-gradient-to-br from-gray-50 to-white hover:shadow-md transition-shadow avoid-break print:border print:shadow-none print:bg-white"
              >
                <p className="font-bold text-sm sm:text-base text-gray-900 mb-1">{project.title}</p>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed whitespace-pre-line text-justify">{project.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CVTemplateModern;
